import { useState } from 'react';
import cc from 'classcat';
import { Checkbox } from '../Checkbox';
import { Input } from '../Input';
import { SelectedItem } from '../SelectedItem';
import { Label } from '../Label';
import { FormikProps } from 'formik';

interface Props {
    label?: string;
    searchPlaceholder: string;
    items: SelectItem[];
    setItems: (items: SelectItem[]) => void;
    selectAllLabel: string;
    deselectAllLabel: string;
    noResultsLabel: string;
    isRequired?: boolean;
    className?: string;
    id?: string;
    disabled?: boolean;
    selectedItemsMaxHeight?: number;
    displaySelected?: 'listBelow' | 'commaSeparated';
    width?: string;
    context?: FormikProps<any>;
    isInvoice?: boolean;
}

export interface SelectItem {
    id: string;
    label: string;
    selected: boolean;
}

const arrowDownSrc = '/svg/arrow-down.svg';
const arrowUpSrc = '/svg/arrow-up.svg';

const determineSelectAllToggleLabel = (items: SelectItem[], selectAllLabel: string, deselectAllLabel: string): string => {
    return items.every(item => item.selected) ? deselectAllLabel : selectAllLabel;
};

const joinSelectedNames = (selected: SelectItem[]) => selected.map(item => item.label).join(', ');

const getInvoiceDisplayValue = (selected: SelectItem[]): string => {
    if (selected.length === 0) return '';
    if (selected.length <= 3) {
        return selected.map(item => item.label).join(', ');
    }
    return (
        selected
            .slice(0, 3)
            .map(item => item.label)
            .join(', ') + '...'
    );
};

export const SearchableMultiSelectDropdown = ({
    label,
    searchPlaceholder,
    items,
    setItems,
    selectAllLabel,
    deselectAllLabel,
    noResultsLabel,
    isRequired,
    id,
    className,
    disabled,
    selectedItemsMaxHeight,
    context,
    displaySelected = 'listBelow',
    width,
    isInvoice = false,
}: Props) => {
    const [openMenu, setOpenMenu] = useState<boolean>(false);
    const [search, setSearch] = useState<string>('');

    const error = context?.errors?.[id as string];
    const submitCount = context?.submitCount || 0;
    const showError = Boolean(error && submitCount > 0);

    const selectAllToggleLabel = determineSelectAllToggleLabel(items, selectAllLabel, deselectAllLabel);

    const searched = items.filter(item => item.label.toLocaleLowerCase().includes(search.toLowerCase()));
    const selected = items.filter(item => item.selected);

    const getPlaceholder = (): string => {
        if (isInvoice && selected.length > 0) {
            return getInvoiceDisplayValue(selected);
        }
        if (selected.length && displaySelected === 'commaSeparated') {
            return joinSelectedNames(selected);
        }
        return searchPlaceholder;
    };

    const placeholder = getPlaceholder();

    const selectAll = () => {
        const isSelectAll = selectAllToggleLabel === selectAllLabel;
        setItems(items.map(item => ({ ...item, selected: isSelectAll })));
    };

    const checkboxChanged = (id: string): void => {
        const newItems = items.map(item => (item.id !== id ? { ...item } : { ...item, selected: !item.selected }));
        setItems(newItems);
    };

    const inputChanged = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const search = e.target.value;
        setSearch(search);
    };

    return (
        <div className={width}>
            <div className={cc([className, 'multi-select-dropdown', openMenu && 'multi-select-dropdown--bubbled'])}>
                {label && <Label label={label} isRequired={isRequired} className="paragraph-4 multi-select-dropdown__select-label" />}
                <Input
                    id={'select_search'}
                    value={search}
                    placeholder={placeholder}
                    onChange={inputChanged}
                    onFocus={() => {
                        setOpenMenu(true);
                        context?.setFieldTouched(id as string, true);
                    }}
                    icon={openMenu ? arrowUpSrc : arrowDownSrc}
                    onIconClick={() => setOpenMenu(prev => !prev)}
                    iconClassName="searchable-multi-select-dropdown__input-icon"
                    autoComplete="off"
                    className={className}
                    disabled={disabled}
                />

                {openMenu && (
                    <>
                        <div className="dropdown__menu multi-select-dropdown__menu">
                            {items.length === 0 ? (
                                <div className="dropdown__menu--empty">No options available</div>
                            ) : !search ? (
                                <>
                                    <p className="multi-select-dropdown__select-all-label paragraph-4" onClick={selectAll}>
                                        {selectAllToggleLabel}
                                    </p>
                                    {searched.map(item => (
                                        <Checkbox
                                            id={item.id}
                                            key={item.id}
                                            label={item.label}
                                            checked={item.selected}
                                            handleChange={() => checkboxChanged(item.id)}
                                        />
                                    ))}
                                </>
                            ) : searched.length === 0 ? (
                                <p className="paragraph-4">{noResultsLabel}</p>
                            ) : (
                                searched.map(item => (
                                    <Checkbox
                                        id={item.id}
                                        key={item.id}
                                        label={item.label}
                                        checked={item.selected}
                                        handleChange={() => checkboxChanged(item.id)}
                                    />
                                ))
                            )}
                        </div>
                        <div className="multi-select-dropdown__event-bubbling-mask" onClick={() => setOpenMenu(false)}></div>
                    </>
                )}
                {showError && <span className={'input-style__error-msg'}>{error as string}</span>}
            </div>

            {displaySelected === 'listBelow' && !isInvoice && (
                <ul
                    className={cc([
                        'searchable-multi-select-dropdown__selected-items',
                        selected.length !== 0 && 'searchable-multi-select-dropdown__selected-items--opened',
                    ])}
                    style={{ maxHeight: selectedItemsMaxHeight ? `${selectedItemsMaxHeight}px` : `none` }}
                >
                    {selected.map(item => (
                        <li key={item.id}>
                            <SelectedItem
                                id={item.id}
                                label={item.label}
                                onDeselect={() => checkboxChanged(item.id)}
                                className="searchable-multi-select-dropdown__selected-item"
                            />
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};
